import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';
import {
  LayoutDashboard, Shield, DollarSign, Users, Heart, Award, Clock,
  Plus, CheckCircle2, Search, FileText, Calculator, BookOpen,
  AlertTriangle, XCircle, Upload, UserPlus, CreditCard, FileCheck,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

// ==================== Mock Data ====================

const KPI = {
  totalFunds: 4,
  totalAUM: 5_690_000,
  totalMembers: 9,
  pensionMembers: 3,
  complianceScore: 88,
  lodgementsDue: 2,
};

const ASSET_ALLOCATION = [
  { name: 'Australian Shares', value: 42, color: '#5048e5' },
  { name: 'International Shares', value: 18, color: '#10b981' },
  { name: 'Property', value: 22, color: '#f59e0b' },
  { name: 'Cash', value: 12, color: '#6366f1' },
  { name: 'Fixed Income', value: 6, color: '#ec4899' },
];

const FUND_SIZES = [
  { name: 'Smith Family', assets: 1_850_000 },
  { name: 'Johnson Retirement', assets: 2_340_000 },
  { name: 'ABC Pty Ltd', assets: 520_000 },
  { name: 'Garcia & Partners', assets: 980_000 },
];

const COMPLIANCE_TABLE = [
  { fund: 'Smith Family Super Fund', score: 95, trustDeed: true, investStrategy: true, bdbn: true, sarLodged: true, minPension: true, iha: true },
  { fund: 'Johnson Retirement Fund', score: 78, trustDeed: true, investStrategy: true, bdbn: false, sarLodged: false, minPension: true, iha: true },
  { fund: 'ABC Pty Ltd Super Fund', score: 52, trustDeed: false, investStrategy: false, sarLodged: false, bdbn: true, minPension: true, iha: false },
  { fund: 'Garcia & Partners Super Fund', score: 92, trustDeed: true, investStrategy: true, bdbn: true, sarLodged: true, minPension: true, iha: true },
];

const URGENT_ITEMS = [
  { date: '20/04/2026', item: 'BDBN Expiry - Mark Johnson', fund: 'Johnson Retirement Fund', urgency: 'critical' as const },
  { date: '28/04/2026', item: 'SAR Due - ABC Pty Ltd Super Fund', fund: 'ABC Pty Ltd Super Fund', urgency: 'critical' as const },
  { date: '15/05/2026', item: 'Minimum Pension Shortfall Review', fund: 'Johnson Retirement Fund', urgency: 'high' as const },
  { date: '30/06/2026', item: 'Investment Strategy Review Due', fund: 'ABC Pty Ltd Super Fund', urgency: 'medium' as const },
  { date: '30/06/2026', item: 'Trust Deed Review', fund: 'ABC Pty Ltd Super Fund', urgency: 'medium' as const },
  { date: '15/07/2026', item: 'SAR Due - Johnson Retirement Fund', fund: 'Johnson Retirement Fund', urgency: 'low' as const },
];

const RECENT_ACTIVITY = [
  { timestamp: '17/04/2026 09:15', action: 'Bank feed synced', fund: 'Smith Family Super Fund', icon: CreditCard },
  { timestamp: '16/04/2026 16:30', action: 'Contribution received ($27,500 SG)', fund: 'Johnson Retirement Fund', icon: DollarSign },
  { timestamp: '16/04/2026 14:00', action: 'Pension payment processed ($4,200)', fund: 'Garcia & Partners Super Fund', icon: Heart },
  { timestamp: '15/04/2026 11:20', action: 'Document uploaded (Investment Strategy)', fund: 'ABC Pty Ltd Super Fund', icon: Upload },
  { timestamp: '14/04/2026 10:00', action: 'Audit completed', fund: 'Smith Family Super Fund', icon: FileCheck },
  { timestamp: '13/04/2026 15:45', action: 'New member added (Peter Johnson)', fund: 'Johnson Retirement Fund', icon: UserPlus },
  { timestamp: '12/04/2026 09:30', action: 'Contribution received ($15,000 SS)', fund: 'Smith Family Super Fund', icon: DollarSign },
  { timestamp: '11/04/2026 14:15', action: 'BDBN renewed', fund: 'Garcia & Partners Super Fund', icon: FileText },
  { timestamp: '10/04/2026 11:00', action: 'Fund registered with ATO', fund: 'ABC Pty Ltd Super Fund', icon: Shield },
  { timestamp: '09/04/2026 16:00', action: 'Pension payment processed ($3,800)', fund: 'Smith Family Super Fund', icon: Heart },
];

const CONTRIBUTION_BY_TYPE = [
  { member: 'J Smith', SG: 27500, SalarySacrifice: 0, PersonalDeductible: 0, NCC: 0, Downsizer: 0 },
  { member: 'J Smith (spouse)', SG: 18200, SalarySacrifice: 5000, PersonalDeductible: 0, NCC: 10000, Downsizer: 0 },
  { member: 'M Johnson', SG: 0, SalarySacrifice: 0, PersonalDeductible: 0, NCC: 110000, Downsizer: 300000 },
  { member: 'S Johnson', SG: 0, SalarySacrifice: 0, PersonalDeductible: 0, NCC: 0, Downsizer: 300000 },
  { member: 'P Johnson', SG: 27500, SalarySacrifice: 0, PersonalDeductible: 0, NCC: 0, Downsizer: 0 },
  { member: 'R Garcia', SG: 22000, SalarySacrifice: 0, PersonalDeductible: 5000, NCC: 0, Downsizer: 0 },
  { member: 'C Garcia', SG: 18500, SalarySacrifice: 3000, PersonalDeductible: 0, NCC: 0, Downsizer: 0 },
];

const CONTRIBUTION_COLORS: Record<string, string> = {
  SG: '#5048e5',
  SalarySacrifice: '#10b981',
  PersonalDeductible: '#f59e0b',
  NCC: '#ec4899',
  Downsizer: '#6366f1',
};

const QUICK_ACTIONS = [
  { label: 'Register New Fund', icon: Plus, path: '/panel/smsf/new' },
  { label: 'Run Compliance Check', icon: Search, path: '/panel/smsf/compliance' },
  { label: 'Generate Audit Pack', icon: FileText, path: '/panel/smsf/reporting' },
  { label: 'View Contributions', icon: DollarSign, path: '/panel/smsf/contributions' },
  { label: 'Pension Calculator', icon: Calculator, path: '/panel/smsf/pensions' },
  { label: 'SMSF Knowledge Base', icon: BookOpen, path: '/panel/smsf/knowledge' },
];

// ==================== Helpers ====================

function ComplianceCheck({ ok }: { ok: boolean }) {
  return ok ? (
    <CheckCircle2 className="h-4 w-4 text-emerald-600 mx-auto" />
  ) : (
    <XCircle className="h-4 w-4 text-red-500 mx-auto" />
  );
}

function getScoreBadge(score: number) {
  if (score >= 80) return { color: 'bg-emerald-100 text-emerald-700', label: `${score}/100` };
  if (score >= 60) return { color: 'bg-amber-100 text-amber-700', label: `${score}/100` };
  return { color: 'bg-red-100 text-red-700', label: `${score}/100` };
}

function getUrgencyColor(urgency: string) {
  switch (urgency) {
    case 'critical': return 'bg-red-100 text-red-700 border-red-200';
    case 'high': return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'medium': return 'bg-blue-100 text-blue-700 border-blue-200';
    default: return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

// ==================== Component ====================

export default function SMSFOverviewDashboard() {
  const navigate = useNavigate();

  const { data: dashboardData, isLoading: dashLoading } = useQuery({
    queryKey: ['smsf-dashboard'],
    queryFn: async () => {
      const r = await apiClient.get('/smsf/dashboard');
      return r.data.data;
    },
  });

  const { data: fundsData, isLoading: fundsLoading } = useQuery({
    queryKey: ['smsf-funds'],
    queryFn: async () => {
      const r = await apiClient.get('/smsf/funds');
      return r.data.data;
    },
  });

  const isLoading = dashLoading || fundsLoading;

  // Merge API data with mock fallbacks
  const kpi = {
    totalFunds: dashboardData?.totalFunds ?? dashboardData?.total_funds ?? KPI.totalFunds,
    totalAUM: dashboardData?.totalAUM ?? dashboardData?.total_assets ?? KPI.totalAUM,
    totalMembers: dashboardData?.totalMembers ?? dashboardData?.total_members ?? KPI.totalMembers,
    pensionMembers: dashboardData?.pensionMembers ?? dashboardData?.pension_members ?? KPI.pensionMembers,
    complianceScore: dashboardData?.complianceScore ?? dashboardData?.compliance_score ?? KPI.complianceScore,
    lodgementsDue: dashboardData?.lodgementsDue ?? dashboardData?.lodgements_due ?? KPI.lodgementsDue,
  };

  const assetAllocation = dashboardData?.assetAllocation ?? dashboardData?.asset_allocation ?? ASSET_ALLOCATION;
  const fundSizes = (fundsData || []).length > 0
    ? (fundsData as any[]).map((f: any) => ({ name: f.fund_name || f.name, assets: f.total_assets || f.totalAssets || 0 }))
    : FUND_SIZES;
  const complianceTable = dashboardData?.complianceTable ?? dashboardData?.compliance_table ?? COMPLIANCE_TABLE;
  const urgentItems = dashboardData?.urgentItems ?? dashboardData?.urgent_items ?? dashboardData?.upcoming_actions ?? URGENT_ITEMS;
  const recentActivity = dashboardData?.recentActivity ?? dashboardData?.recent_activity ?? RECENT_ACTIVITY;
  const contributionByType = dashboardData?.contributionByType ?? dashboardData?.contribution_by_type ?? CONTRIBUTION_BY_TYPE;

  const complianceBadge = getScoreBadge(kpi.complianceScore);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={LayoutDashboard}
        title="SMSF Dashboard"
        description="Firm-wide SMSF portfolio overview"
      />

      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading dashboard...</span>
        </div>
      )}

      {/* Row 1: KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#5048e5]/10 text-[#5048e5] flex items-center justify-center">
                <Shield className="h-4.5 w-4.5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Funds</p>
                <p className="text-xl font-bold">{kpi.totalFunds}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                <DollarSign className="h-4.5 w-4.5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total AUM</p>
                <p className="text-xl font-bold">${(kpi.totalAUM / 1_000_000).toFixed(1)}M</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-blue-500/10 text-blue-600 flex items-center justify-center">
                <Users className="h-4.5 w-4.5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Members</p>
                <p className="text-xl font-bold">{kpi.totalMembers}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-pink-500/10 text-pink-600 flex items-center justify-center">
                <Heart className="h-4.5 w-4.5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">In Pension</p>
                <p className="text-xl font-bold">{kpi.pensionMembers}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center', kpi.complianceScore >= 80 ? 'bg-emerald-500/10 text-emerald-600' : kpi.complianceScore >= 60 ? 'bg-amber-500/10 text-amber-600' : 'bg-red-500/10 text-red-600')}>
                <Award className="h-4.5 w-4.5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Compliance</p>
                <p className={cn('text-xl font-bold', kpi.complianceScore >= 80 ? 'text-emerald-600' : kpi.complianceScore >= 60 ? 'text-amber-600' : 'text-red-600')}>
                  {kpi.complianceScore}/100
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center">
                <Clock className="h-4.5 w-4.5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Lodgements Due</p>
                <p className="text-xl font-bold">{kpi.lodgementsDue}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Asset Allocation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={assetAllocation}
                    cx="35%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    activeShape={null as any}
                  >
                    {assetAllocation.map((entry: any, idx: number) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `${v}%`} cursor={false} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Fund Sizes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={fundSizes}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1_000_000).toFixed(1)}M`} />
                  <Tooltip formatter={(v) => `$${Number(v).toLocaleString('en-AU')}`} cursor={false} />
                  <Bar dataKey="assets" fill="#5048e5" radius={[4, 4, 0, 0]} name="Total Assets" activeBar={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Compliance Overview */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Compliance Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Fund</th>
                  <th className="pb-2 pr-4 font-medium text-center">Score</th>
                  <th className="pb-2 pr-4 font-medium text-center">Trust Deed</th>
                  <th className="pb-2 pr-4 font-medium text-center">Invest. Strategy</th>
                  <th className="pb-2 pr-4 font-medium text-center">BDBN Current</th>
                  <th className="pb-2 pr-4 font-medium text-center">SAR Lodged</th>
                  <th className="pb-2 pr-4 font-medium text-center">Min Pension</th>
                  <th className="pb-2 font-medium text-center">IHA &lt; 5%</th>
                </tr>
              </thead>
              <tbody>
                {complianceTable.map((row: any, i: number) => {
                  const badge = getScoreBadge(row.score);
                  return (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2.5 pr-4 font-medium text-xs">{row.fund}</td>
                      <td className="py-2.5 pr-4 text-center">
                        <span className={cn('inline-block px-2 py-0.5 rounded-full text-xs font-medium', badge.color)}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4"><ComplianceCheck ok={row.trustDeed} /></td>
                      <td className="py-2.5 pr-4"><ComplianceCheck ok={row.investStrategy} /></td>
                      <td className="py-2.5 pr-4"><ComplianceCheck ok={row.bdbn} /></td>
                      <td className="py-2.5 pr-4"><ComplianceCheck ok={row.sarLodged} /></td>
                      <td className="py-2.5 pr-4"><ComplianceCheck ok={row.minPension} /></td>
                      <td className="py-2.5"><ComplianceCheck ok={row.iha} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Row 4: Urgent Items + Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Urgent Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {urgentItems.map((item: any, i: number) => (
                <div key={i} className={cn('flex items-start gap-3 p-2.5 rounded-lg border', getUrgencyColor(item.urgency))}>
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold">{item.item}</p>
                    <p className="text-[11px] opacity-80">{item.fund}</p>
                  </div>
                  <span className="text-[11px] font-medium whitespace-nowrap">{item.date}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentActivity.map((item: any, i: number) => {
                const Icon = item.icon || Clock;
                return (
                  <div key={i} className="flex items-start gap-3 py-1.5">
                    <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium">{item.action}</p>
                      <p className="text-[11px] text-muted-foreground">{item.fund}</p>
                    </div>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">{item.timestamp}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 5: Contribution Summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">FY2025-26 Contribution Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={contributionByType}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="member" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString('en-AU')}`} cursor={false} />
                <Legend />
                <Bar dataKey="SG" stackId="a" fill={CONTRIBUTION_COLORS.SG} name="SG" activeBar={false}/>
                <Bar dataKey="SalarySacrifice" stackId="a" fill={CONTRIBUTION_COLORS.SalarySacrifice} name="Salary Sacrifice" activeBar={false}/>
                <Bar dataKey="PersonalDeductible" stackId="a" fill={CONTRIBUTION_COLORS.PersonalDeductible} name="Personal Deductible" activeBar={false}/>
                <Bar dataKey="NCC" stackId="a" fill={CONTRIBUTION_COLORS.NCC} name="Non-Concessional" activeBar={false}/>
                <Bar dataKey="Downsizer" stackId="a" fill={CONTRIBUTION_COLORS.Downsizer} name="Downsizer" activeBar={false}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Row 6: Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {QUICK_ACTIONS.map((action, i) => {
          const Icon = action.icon;
          return (
            <Button
              key={i}
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate(action.path)}
            >
              <Icon className="h-5 w-5" />
              <span className="text-xs text-center leading-tight">{action.label}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
